import { Box, Chip } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";

interface Props {
  categories: string[];
  activeIndex: number;
  onCategoryClick: (index: number) => void;
}

const FADE_WIDTH = 32;
const BG = "#f5f2e5";

const CategoryNav: FC<Props> = ({
  categories,
  activeIndex,
  onCategoryClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateFades = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    updateFades();
    el.addEventListener("scroll", updateFades);
    window.addEventListener("resize", updateFades);
    return () => {
      el.removeEventListener("scroll", updateFades);
      window.removeEventListener("resize", updateFades);
    };
  }, [updateFades, categories]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const activeChip = container.children[activeIndex] as
      | HTMLElement
      | undefined;
    if (activeChip) {
      const scrollLeft =
        activeChip.offsetLeft -
        container.offsetWidth / 2 +
        activeChip.offsetWidth / 2;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, [activeIndex]);

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
      }}
    >
      {canScrollLeft && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: FADE_WIDTH,
            background: `linear-gradient(to right, ${BG}, transparent)`,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}
      {canScrollRight && (
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: FADE_WIDTH,
            background: `linear-gradient(to left, ${BG}, transparent)`,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}
      <Box
        id="category-nav"
        ref={containerRef}
        sx={{
          display: "flex",
          gap: 1,
          overflowX: "auto",
          alignItems: "center",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {categories.map((name, index) => (
          <Chip
            key={name}
            label={name}
            onClick={() => onCategoryClick(index)}
            sx={{
              flexShrink: 0,
              cursor: "pointer",
              fontWeight: index === activeIndex ? 600 : 400,
              ...(index === activeIndex
                ? {
                    backgroundColor: "#447737",
                    color: "#f5f2e5",
                    "&:hover": { backgroundColor: "#3a6530" },
                  }
                : {
                    backgroundColor: "transparent",
                    border: "1px solid #447737",
                    color: "#447737",
                    "&:hover": { backgroundColor: "rgba(68, 119, 55, 0.08)" },
                  }),
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CategoryNav;
